import { useState, type FormEvent, type KeyboardEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertCircle, Eye, EyeOff, Loader2, Lock } from "lucide-react";
import ServiceDeskAPIService from "@sd/services/APIs";
import { sd } from "@sd/lib/sdPaths";
import {
  SdAuthShell,
  sdAuthInputClassName,
  sdAuthLabelClassName,
  sdAuthPrimaryButtonClassName,
} from "@sd/components/auth/SdAuthShell";
import { PasswordRequirementsPanel } from "@sd/components/auth/PasswordRequirementsPanel";
import { parseSdAuthApiError } from "@sd/components/auth/parseSdAuthApiError";
import { cn } from "@/lib/utils";
import {
  SD_PASSWORD_MAX,
  capSdPassword,
  getSdPasswordLiveErrors,
  validateSdNewPassword,
} from "@sd/lib/sdPasswordRules";

function blockOverMaxKeys(event: KeyboardEvent<HTMLInputElement>) {
  const allowed = [
    "Backspace",
    "Delete",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
    "Tab",
  ];
  if (event.ctrlKey || event.metaKey || event.altKey) return;
  if (allowed.includes(event.key)) return;
  const input = event.currentTarget;
  if (input.value.length >= SD_PASSWORD_MAX && input.selectionStart === input.selectionEnd) {
    event.preventDefault();
  }
}

export default function SDForgotPasswordReset() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const resetId = (id ?? "").trim();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showRequirements, setShowRequirements] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const liveErrors = getSdPasswordLiveErrors(newPassword);
  const confirmMismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;
  const newPasswordInvalid = liveErrors.length > 0;
  const errorInputClass =
    "border-red-400 bg-white hover:border-red-400 focus:border-red-500 focus:bg-white focus:ring-red-500/15";

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");

    if (!resetId) {
      setError("This reset link is invalid or incomplete.");
      return;
    }
    const passwordError = validateSdNewPassword(newPassword);
    if (passwordError || liveErrors.length > 0) {
      setError("");
      return;
    }
    if (!confirmPassword) {
      setError("Confirm password is required.");
      return;
    }
    if (confirmMismatch || newPassword !== confirmPassword) {
      return;
    }

    setLoading(true);
    try {
      await ServiceDeskAPIService.auth.forgotPasswordReset(
        resetId,
        { new_password: newPassword, confirm_password: confirmPassword },
        { skipErrorNotification: true }
      );
      navigate(sd("login"), { replace: true });
    } catch (err: unknown) {
      setError(parseSdAuthApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <SdAuthShell>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900">Reset Password</h2>
        <p className="mt-1 text-sm text-slate-500">Enter your new password</p>
      </div>

      {error ? (
        <div
          className="mb-5 flex gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3.5 text-sm text-red-800"
          role="alert"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden />
          <p className="leading-relaxed">{error}</p>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <div className="mb-2 flex items-center justify-between gap-2">
            <label htmlFor="sd-reset-new-password" className="text-[13px] font-medium text-slate-600">
              New Password
            </label>
            <button
              type="button"
              onClick={() => setShowRequirements(v => !v)}
              className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
            >
              {showRequirements ? "Hide Requirements" : "Show Requirements"}
            </button>
          </div>
          <div className="relative">
            <Lock
              className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400"
              strokeWidth={1.75}
              aria-hidden
            />
            <input
              id="sd-reset-new-password"
              type={showNew ? "text" : "password"}
              value={newPassword}
              onChange={e => setNewPassword(capSdPassword(e.target.value))}
              onKeyDown={blockOverMaxKeys}
              required
              maxLength={SD_PASSWORD_MAX}
              autoComplete="new-password"
              placeholder={`Enter new password (8-${SD_PASSWORD_MAX} characters)`}
              className={cn(
                sdAuthInputClassName,
                "pr-12",
                newPasswordInvalid && errorInputClass
              )}
            />
            <button
              type="button"
              onClick={() => setShowNew(v => !v)}
              className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
              aria-label={showNew ? "Hide new password" : "Show new password"}
            >
              {showNew ? (
                <EyeOff className="h-[18px] w-[18px]" strokeWidth={1.75} />
              ) : (
                <Eye className="h-[18px] w-[18px]" strokeWidth={1.75} />
              )}
            </button>
          </div>
          {liveErrors.length > 0 ? (
            <div
              className="mt-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] leading-relaxed text-red-700"
              role="status"
            >
              <ul className="list-disc space-y-1 pl-4">
                {liveErrors.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        {showRequirements ? <PasswordRequirementsPanel /> : null}

        <div>
          <label htmlFor="sd-reset-confirm-password" className={sdAuthLabelClassName}>
            Confirm Password
          </label>
          <div className="relative">
            <Lock
              className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400"
              strokeWidth={1.75}
              aria-hidden
            />
            <input
              id="sd-reset-confirm-password"
              type={showConfirm ? "text" : "password"}
              value={confirmPassword}
              onChange={e => setConfirmPassword(capSdPassword(e.target.value))}
              onKeyDown={blockOverMaxKeys}
              required
              maxLength={SD_PASSWORD_MAX}
              autoComplete="new-password"
              placeholder="Confirm your new password"
              className={cn(
                sdAuthInputClassName,
                "pr-12",
                confirmMismatch && errorInputClass
              )}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(v => !v)}
              className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
              aria-label={
                showConfirm ? "Hide confirm password" : "Show confirm password"
              }
            >
              {showConfirm ? (
                <EyeOff className="h-[18px] w-[18px]" strokeWidth={1.75} />
              ) : (
                <Eye className="h-[18px] w-[18px]" strokeWidth={1.75} />
              )}
            </button>
          </div>
          {confirmMismatch ? (
            <p className="mt-2 text-[13px] text-red-500">Passwords do not match!</p>
          ) : null}
        </div>

        <button type="submit" disabled={loading} className={sdAuthPrimaryButtonClassName}>
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Resetting…
            </>
          ) : (
            "Reset Password"
          )}
        </button>
      </form>

      <p className="mt-5 text-center">
        <Link
          to={sd("login")}
          className="text-sm font-semibold text-slate-800 hover:text-blue-700"
        >
          Back to Sign In
        </Link>
      </p>
    </SdAuthShell>
  );
}
